/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the "Elastic License
 * 2.0", the "GNU Affero General Public License v3.0 only", and the "Server Side
 * Public License v 1"; you may not use this file except in compliance with, at
 * your election, the "Elastic License 2.0", the "GNU Affero General Public
 * License v3.0 only", or the "Server Side Public License, v 1".
 */

import { ParserRuleContext } from 'antlr4';
import { default as esql_parser, ArithmeticBinaryContext, ArithmeticUnaryContext, BooleanArrayLiteralContext, BooleanDefaultContext, BooleanLiteralContext, InputParameterContext, BooleanValueContext, ComparisonContext, ConstantDefaultContext, DecimalLiteralContext, DereferenceContext, FunctionContext, IntegerLiteralContext, IsNullContext, LogicalBinaryContext, LogicalInContext, LogicalNotContext, MetadataContext, MvExpandCommandContext, NullLiteralContext, NumericArrayLiteralContext, NumericValueContext, OperatorExpressionDefaultContext, ParenthesizedExpressionContext, QualifiedIntegerLiteralContext, RegexBooleanExpressionContext, StringArrayLiteralContext, StringContext, StringLiteralContext, ValueExpressionDefaultContext, InlineCastContext, IndexPatternContext, MatchExpressionContext } from '../antlr/esql_parser';
import { createSource, createColumn, createOption, nonNullable, createFunction, createLiteral, createTimeUnit, createFakeMultiplyLiteral, createList, createNumericLiteral, computeLocationExtends, createColumnStar, wrapIdentifierAsArray, createPolicy, createSetting, textExistsAndIsValid, createInlineCast, createUnknownItem, createOrderExpression, createFunctionCall, createParam, createLiteralString, createBinaryExpression } from './factories';
import { firstItem, lastItem } from '../visitor/utils';
import { Builder } from '../builder';
import { getPosition } from './helpers';
export function collectAllSourceIdentifiers(ctx) {
  const fromContexts = ctx.indexPatternAndMetadataFields().getTypedRuleContexts(IndexPatternContext);
  return fromContexts.map(sourceCtx => createSource(sourceCtx));
}
function terminalNodeToParserRuleContext(node) {
  const context = new ParserRuleContext();
  context.start = node.symbol;
  context.stop = node.symbol;
  context.children = [node];
  return context;
}
function extractIdentifiers(ctx) {
  if (ctx instanceof MetadataContext) {
    return ctx.UNQUOTED_SOURCE_list().map(node => {
      return terminalNodeToParserRuleContext(node);
    }).flat();
  }
  if (ctx instanceof MvExpandCommandContext) {
    return wrapIdentifierAsArray(ctx.qualifiedName());
  }
  return wrapIdentifierAsArray(ctx.qualifiedNamePatterns().qualifiedNamePattern_list());
}
function makeColumnsOutOfIdentifiers(identifiers) {
  var _identifiers$filter$m;
  const args = (_identifiers$filter$m = identifiers.filter(child => textExistsAndIsValid(child.getText())).map(sourceContext => {
    return createColumn(sourceContext);
  })) !== null && _identifiers$filter$m !== void 0 ? _identifiers$filter$m : [];
  return args;
}
export function collectAllColumnIdentifiers(ctx) {
  const identifiers = extractIdentifiers(ctx);
  return makeColumnsOutOfIdentifiers(identifiers);
}
export function getPolicyName(ctx) {
  if (!ctx._policyName || !textExistsAndIsValid(ctx._policyName.text)) {
    return [];
  }
  const policyComponents = ctx._policyName.text.split(':');
  if (policyComponents.length > 1) {
    const [setting, policyName] = policyComponents;
    return [createSetting(ctx._policyName, setting), createPolicy(ctx._policyName, policyName)];
  }
  return [createPolicy(ctx._policyName, policyComponents[0])];
}
export function getMatchField(ctx) {
  if (!ctx._matchField) {
    return [];
  }
  const identifier = ctx.qualifiedNamePattern();
  if (identifier) {
    const fn = createOption(ctx.ON().getText().toLowerCase(), ctx);
    let max = ctx.ON().symbol.stop;
    if (textExistsAndIsValid(identifier.getText())) {
      const column = createColumn(identifier);
      fn.args.push(column);
      max = column.location.max;
    }
    fn.location.min = ctx.ON().symbol.start;
    fn.location.max = max;
    return [fn];
  }
  return [];
}
export function getEnrichClauses(ctx) {
  const ast = [];
  const withCtx = ctx.WITH();
  if (withCtx) {
    const option = createOption(withCtx.getText().toLowerCase(), ctx);
    ast.push(option);
    const clauses = ctx.enrichWithClause_list();
    for (const clause of clauses) {
      var _lastArg$location$max, _lastArg$location;
      if (clause._enrichField) {
        const args = [];
        if (clause.ASSIGN()) {
          var _clause$_enrichField;
          args.push(createColumn(clause._newName));
          if (textExistsAndIsValid((_clause$_enrichField = clause._enrichField) === null || _clause$_enrichField === void 0 ? void 0 : _clause$_enrichField.getText())) {
            args.push(createColumn(clause._enrichField));
          }
        } else {
          var _clause$_enrichField2;
          // if an explicit assign is not set, create a fake assign with
          // both left and right value with the same column
          if (textExistsAndIsValid((_clause$_enrichField2 = clause._enrichField) === null || _clause$_enrichField2 === void 0 ? void 0 : _clause$_enrichField2.getText())) {
            args.push(createColumn(clause._enrichField), createColumn(clause._enrichField));
          }
        }
        if (args.length) {
          const fn = createFunction('=', clause, undefined, 'binary-expression');
          fn.args.push(args[0], args[1] ? [args[1]] : []);
          option.args.push(fn);
        }
      }
      const location = option.location;
      const lastArg = lastItem(option.args);
      location.min = withCtx.symbol.start;
      location.max = (_lastArg$location$max = lastArg === null || lastArg === void 0 ? void 0 : (_lastArg$location = lastArg.location) === null || _lastArg$location === void 0 ? void 0 : _lastArg$location.max) !== null && _lastArg$location$max !== void 0 ? _lastArg$location$max : withCtx.symbol.stop;
    }
  }
  return ast;
}
function visitLogicalNot(ctx) {
  const fn = createFunction('not', ctx, undefined, 'unary-expression');
  fn.args.push(...collectBooleanExpression(ctx.booleanExpression()));
  // update the location of the assign based on arguments
  const argsLocationExtends = computeLocationExtends(fn);
  fn.location = argsLocationExtends;
  return fn;
}
function visitLogicalAndsOrs(ctx) {
  const fn = createFunction(ctx.AND() ? 'and' : 'or', ctx, undefined, 'binary-expression');
  fn.args.push(...collectBooleanExpression(ctx._left), ...collectBooleanExpression(ctx._right));
  // update the location of the assign based on arguments
  const argsLocationExtends = computeLocationExtends(fn);
  fn.location = argsLocationExtends;
  return fn;
}
function visitLogicalIns(ctx) {
  const fn = createFunction(ctx.NOT() ? 'not_in' : 'in', ctx, undefined, 'binary-expression');
  const [left, ...list] = ctx.valueExpression_list();
  const leftArg = visitValueExpression(left);
  if (leftArg) {
    fn.args.push(...(Array.isArray(leftArg) ? leftArg : [leftArg]));
    const values = list.map(ve => visitValueExpression(ve));
    const listArgs = values.filter(nonNullable).flatMap(arg => Array.isArray(arg) ? arg.filter(nonNullable) : arg);
    // distinguish between missing brackets (missing text error) and an empty list
    if (textExistsAndIsValid(ctx.getText())) {
      fn.args.push(listArgs);
    }
  }
  // update the location of the assign based on arguments
  const argsLocationExtends = computeLocationExtends(fn);
  fn.location = argsLocationExtends;
  return fn;
}
function getMathOperation(ctx) {
  return (ctx.PLUS() || ctx.MINUS() || ctx.ASTERISK() || ctx.SLASH() || ctx.PERCENT()).getText() || '';
}
function getComparisonName(ctx) {
  return (ctx.EQ() || ctx.NEQ() || ctx.LT() || ctx.LTE() || ctx.GT() || ctx.GTE()).getText() || '';
}
export function visitValueExpression(ctx) {
  if (!textExistsAndIsValid(ctx.getText())) {
    return [];
  }
  if (ctx instanceof ValueExpressionDefaultContext) {
    return visitOperatorExpression(ctx.operatorExpression());
  }
  if (ctx instanceof ComparisonContext) {
    const comparisonNode = ctx.comparisonOperator();
    const comparisonFn = createFunction(getComparisonName(comparisonNode), comparisonNode, undefined, 'binary-expression');
    comparisonFn.args.push(visitOperatorExpression(ctx._left), visitOperatorExpression(ctx._right));
    // update the location of the comparisonFn based on arguments
    const argsLocationExtends = computeLocationExtends(comparisonFn);
    comparisonFn.location = argsLocationExtends;
    return comparisonFn;
  }
}
function visitOperatorExpression(ctx) {
  if (ctx instanceof ArithmeticUnaryContext) {
    const arg = visitOperatorExpression(ctx.operatorExpression());
    // this is a number sign thing
    const fn = createFunction('*', ctx, undefined, 'binary-expression');
    fn.args.push(createFakeMultiplyLiteral(ctx, 'integer'));
    if (arg) {
      fn.args.push(arg);
    }
    return fn;
  } else if (ctx instanceof ArithmeticBinaryContext) {
    const fn = createFunction(getMathOperation(ctx), ctx, undefined, 'binary-expression');
    const args = [visitOperatorExpression(ctx._left), visitOperatorExpression(ctx._right)];
    for (const arg of args) {
      if (arg) {
        fn.args.push(arg);
      }
    }
    // update the location of the assign based on arguments
    const argsLocationExtends = computeLocationExtends(fn);
    fn.location = argsLocationExtends;
    return fn;
  } else if (ctx instanceof OperatorExpressionDefaultContext) {
    return visitPrimaryExpression(ctx.primaryExpression());
  }
}
function getBooleanValue(ctx) {
  const parentNode = ctx instanceof BooleanLiteralContext ? ctx.booleanValue() : ctx;
  const booleanTerminalNode = parentNode.TRUE() || parentNode.FALSE();
  return createLiteral('boolean', booleanTerminalNode);
}
export function getConstant(ctx) {
  if (ctx instanceof NullLiteralContext) {
    return createLiteral('null', ctx.NULL());
  }
  if (ctx instanceof QualifiedIntegerLiteralContext) {
    // despite the generic name, this is a date unit constant:
    // e.g. 1 year, 15 months
    return createTimeUnit(ctx);
  }

  // Decimal type covers multiple ES|QL types: long, double, etc.
  if (ctx instanceof DecimalLiteralContext) {
    return createNumericLiteral(ctx.decimalValue(), 'double');
  }

  // Integer type encompasses integer
  if (ctx instanceof IntegerLiteralContext) {
    return createNumericLiteral(ctx.integerValue(), 'integer');
  }
  if (ctx instanceof BooleanLiteralContext) {
    return getBooleanValue(ctx);
  }
  if (ctx instanceof StringLiteralContext) {
    return createLiteralString(ctx.string_());
  }
  if (ctx instanceof NumericArrayLiteralContext || ctx instanceof BooleanArrayLiteralContext || ctx instanceof StringArrayLiteralContext) {
    const values = [];
    for (const numericValue of ctx.getTypedRuleContexts(NumericValueContext)) {
      const isDecimal = numericValue.decimalValue() !== null && numericValue.decimalValue() !== undefined;
      const value = numericValue.decimalValue() || numericValue.integerValue();
      values.push(createNumericLiteral(value, isDecimal ? 'double' : 'integer'));
    }
    for (const booleanValue of ctx.getTypedRuleContexts(BooleanValueContext)) {
      values.push(getBooleanValue(booleanValue));
    }
    for (const string of ctx.getTypedRuleContexts(StringContext)) {
      const literal = createLiteralString(string);
      values.push(literal);
    }
    return createList(ctx, values);
  }
  if (ctx instanceof InputParameterContext && ctx.children) {
    const values = [];
    for (const child of ctx.children) {
      const param = createParam(child);
      if (param) values.push(param);
    }
    return values;
  }
  return createUnknownItem(ctx);
}
export function visitRenameClauses(clausesCtx) {
  return clausesCtx.map(clause => {
    var _clause$_oldName;
    const asToken = clause.getToken(esql_parser.AS, 0);
    if (asToken && textExistsAndIsValid(asToken.getText())) {
      const option = createOption(asToken.getText().toLowerCase(), clause);
      for (const arg of [clause._oldName, clause._newName]) {
        if (textExistsAndIsValid(arg.getText())) {
          option.args.push(createColumn(arg));
        }
      }
      const firstArg = firstItem(option.args);
      const lastArg = lastItem(option.args);
      const location = option.location;
      if (firstArg) location.min = firstArg.location.min;
      if (lastArg) location.max = lastArg.location.max;
      return option;
    } else if (textExistsAndIsValid((_clause$_oldName = clause._oldName) === null || _clause$_oldName === void 0 ? void 0 : _clause$_oldName.getText())) {
      return createColumn(clause._oldName);
    }
  }).filter(nonNullable);
}
export function visitPrimaryExpression(ctx) {
  if (ctx instanceof ConstantDefaultContext) {
    return getConstant(ctx.constant());
  } else if (ctx instanceof DereferenceContext) {
    return createColumn(ctx.qualifiedName());
  } else if (ctx instanceof ParenthesizedExpressionContext) {
    return collectBooleanExpression(ctx.booleanExpression());
  } else if (ctx instanceof FunctionContext) {
    const functionExpressionCtx = ctx.functionExpression();
    const fn = createFunctionCall(ctx);
    const asteriskArg = functionExpressionCtx.ASTERISK() ? createColumnStar(functionExpressionCtx.ASTERISK()) : undefined;
    if (asteriskArg) {
      fn.args.push(asteriskArg);
    }
    const functionArgs = functionExpressionCtx.booleanExpression_list().flatMap(collectBooleanExpression).filter(nonNullable);
    if (functionArgs.length) {
      fn.args.push(...functionArgs);
    }
    const mapExpressionCtx = functionExpressionCtx.mapExpression();
    if (mapExpressionCtx) {
      const trailingMap = visitMapExpression(mapExpressionCtx);
      fn.args.push(trailingMap);
    }
    return fn;
  } else if (ctx instanceof InlineCastContext) {
    return collectInlineCast(ctx);
  }
  return createUnknownItem(ctx);
}
export const visitMapExpression = ctx => {
  const map = Builder.expression.map({}, {
    location: getPosition(ctx.start, ctx.stop),
    incomplete: Boolean(ctx.exception)
  });
  const entryCtxs = ctx.entryExpression_list();
  for (const entryCtx of entryCtxs) {
    const entry = visitMapEntryExpression(entryCtx);
    map.entries.push(entry);
  }
  return map;
};
export const visitMapEntryExpression = ctx => {
  const keyCtx = ctx._key;
  const valueCtx = ctx._value;
  const key = createLiteralString(keyCtx);
  const value = getConstant(valueCtx);
  const entry = Builder.expression.entry(key, value, {
    location: getPosition(ctx.start, ctx.stop),
    incomplete: Boolean(ctx.exception)
  });
  return entry;
};
function collectInlineCast(ctx) {
  const primaryExpression = visitPrimaryExpression(ctx.primaryExpression());
  return createInlineCast(ctx, primaryExpression);
}
export function collectLogicalExpression(ctx) {
  if (ctx instanceof LogicalNotContext) {
    return [visitLogicalNot(ctx)];
  }
  if (ctx instanceof LogicalBinaryContext) {
    return [visitLogicalAndsOrs(ctx)];
  }
  if (ctx instanceof LogicalInContext) {
    return [visitLogicalIns(ctx)];
  }
  return [];
}
function collectRegexExpression(ctx) {
  const regexes = ctx.getTypedRuleContexts(RegexBooleanExpressionContext);
  const ret = [];
  return ret.concat(regexes.map(regex => {
    var _regex$_kind$text;
    const negate = regex.NOT();
    const likeType = ((_regex$_kind$text = regex._kind.text) === null || _regex$_kind$text === void 0 ? void 0 : _regex$_kind$text.toLowerCase()) || '';
    const fnName = `${negate ? 'not_' : ''}${likeType}`;
    const fn = createFunction(fnName, regex, undefined, 'binary-expression');
    const arg = visitValueExpression(regex.valueExpression());
    if (arg) {
      fn.args.push(arg);
      const literal = createLiteralString(regex._pattern);
      fn.args.push(literal);
    }
    return fn;
  }));
}
function collectIsNullExpression(ctx) {
  if (!(ctx instanceof IsNullContext)) {
    return [];
  }
  const negate = ctx.NOT();
  const fnName = `is${negate ? ' not ' : ' '}null`;
  const fn = createFunction(fnName, ctx, undefined, 'postfix-unary-expression');
  const arg = visitValueExpression(ctx.valueExpression());
  if (arg) {
    fn.args.push(arg);
  }
  return [fn];
}
function collectDefaultExpression(ctx) {
  if (!(ctx instanceof BooleanDefaultContext)) {
    return [];
  }
  const arg = visitValueExpression(ctx.valueExpression());
  return arg ? [arg] : [];
}
export function collectBooleanExpression(ctx) {
  const ast = [];
  if (!ctx) {
    return ast;
  }
  if (ctx instanceof MatchExpressionContext) {
    return [visitMatchExpression(ctx)];
  }
  return ast.concat(collectLogicalExpression(ctx), collectRegexExpression(ctx), collectIsNullExpression(ctx), collectDefaultExpression(ctx)).flat();
}
const visitMatchExpression = ctx => {
  return visitMatchBooleanExpression(ctx.matchBooleanExpression());
};
const visitMatchBooleanExpression = ctx => {
  let expression = createColumn(ctx.qualifiedName());
  const dataTypeCtx = ctx.dataType();
  const constantCtx = ctx.constant();
  if (dataTypeCtx) {
    expression = Builder.expression.inlineCast({
      castType: dataTypeCtx.getText().toLowerCase(),
      value: expression
    }, {
      location: getPosition(ctx.start, dataTypeCtx.stop),
      incomplete: Boolean(ctx.exception)
    });
  }
  if (constantCtx) {
    const constantExpression = getConstant(constantCtx);
    expression = createBinaryExpression(':', ctx, [expression, constantExpression]);
  }
  return expression;
};
export function visitField(ctx) {
  if (ctx.qualifiedName() && ctx.ASSIGN()) {
    const fn = createFunction(ctx.ASSIGN().getText(), ctx, undefined, 'binary-expression');
    fn.args.push(createColumn(ctx.qualifiedName()), collectBooleanExpression(ctx.booleanExpression()));
    // update the location of the assign based on arguments
    const argsLocationExtends = computeLocationExtends(fn);
    fn.location = argsLocationExtends;
    return [fn];
  }
  return collectBooleanExpression(ctx.booleanExpression());
}
export function collectAllAggFields(ctx) {
  const ast = [];
  if (!ctx) {
    return ast;
  }
  try {
    for (const aggField of ctx.aggField_list()) {
      ast.push(...visitField(aggField.field()));
    }
  } catch (e) {
    // do nothing
  }
  return ast;
}
export function collectAllFields(ctx) {
  const ast = [];
  if (!ctx) {
    return ast;
  }
  try {
    for (const field of ctx.field_list()) {
      ast.push(...visitField(field));
    }
  } catch (e) {
    // do nothing
  }
  return ast;
}
export function visitByOption(ctx, expr) {
  const byCtx = ctx.BY();
  if (!byCtx || !expr) {
    return [];
  }
  const option = createOption(byCtx.getText().toLowerCase(), ctx);
  option.args.push(...collectAllFields(expr));
  option.location.min = byCtx.symbol.start;
  const lastArg = lastItem(option.args);
  if (lastArg) option.location.max = lastArg.location.max;
  return [option];
}
const visitOrderExpression = ctx => {
  var _ctx$_ordering, _ctx$_ordering$text, _ctx$_nullOrdering, _ctx$_nullOrdering$te;
  const arg = collectBooleanExpression(ctx.booleanExpression())[0];
  let order = '';
  let nulls = '';
  const ordering = (_ctx$_ordering = ctx._ordering) === null || _ctx$_ordering === void 0 ? void 0 : (_ctx$_ordering$text = _ctx$_ordering.text) === null || _ctx$_ordering$text === void 0 ? void 0 : _ctx$_ordering$text.toUpperCase();
  if (ordering) order = ordering;
  const nullOrdering = (_ctx$_nullOrdering = ctx._nullOrdering) === null || _ctx$_nullOrdering === void 0 ? void 0 : (_ctx$_nullOrdering$te = _ctx$_nullOrdering.text) === null || _ctx$_nullOrdering$te === void 0 ? void 0 : _ctx$_nullOrdering$te.toUpperCase();
  switch (nullOrdering) {
    case 'LAST':
      nulls = 'NULLS LAST';
      break;
    case 'FIRST':
      nulls = 'NULLS FIRST';
      break;
  }
  if (!order && !nulls) {
    return arg;
  }
  return createOrderExpression(ctx, arg, order, nulls);
};
export function visitOrderExpressions(ctx) {
  const ast = [];
  for (const orderCtx of ctx) {
    ast.push(visitOrderExpression(orderCtx));
  }
  return ast;
}